// The flowchart canvas's own vocabulary — the values a box is drawn with. Shared because three
// places need the same numbers and the same palette: the canvas that renders a box, the drafting
// package that proposes one, and the doc layer that writes one.

/** Positions and widths snap to this grid. */
export const GRID_PIXELS = 20;

/** Edge vertices snap to a finer grid than boxes do. */
export const EDGE_GRID_PIXELS = 5;

export const NODE_WIDTH_MIN = 160;
export const NODE_WIDTH_MAX = 800;
export const NODE_WIDTH_DEFAULT = 240;

/** The palette a box is drawn in, as the canvas paints it. */
export const NODE_COLORS = Object.freeze({
  WHITE: {
    backgroundColor: '#fafafa',
    borderColor: '#bdbdbd'
  },
  BLUE: {
    backgroundColor: '#f1fdff',
    borderColor: '#77c9f2'
  },
  YELLOW: {
    backgroundColor: '#fffdd8',
    borderColor: '#ffe171'
  },
  ORANGE: {
    backgroundColor: '#ffe5c8',
    borderColor: '#ffb573'
  },
  RED: {
    backgroundColor: '#ffe6ea',
    borderColor: '#ff8d80'
  },
  GREEN: {
    backgroundColor: '#e5f7cd',
    borderColor: '#6cba08'
  },
  PURPLE: {
    backgroundColor: '#f2f0fa',
    borderColor: '#7b69d4'
  },
  BROWN: {
    backgroundColor: '#f7f0f1',
    borderColor: '#b36d72'
  }
});

export type NodeColor = keyof typeof NODE_COLORS;

/** Where every box starts. A box drawn in it is one nobody chose a colour for. */
export const DEFAULT_NODE_COLOR = 'WHITE' satisfies NodeColor;

/** The palette keys, for anything that names a colour rather than painting one. */
export const NODE_COLOR_KEYS = Object.keys(NODE_COLORS) as NodeColor[];

/** The tiptap node type for an inline colour swatch in overview legends. */
export const LEGEND_SWATCH_NODE = 'legendSwatch';

/** The fill and border pairs a legend swatch may carry, one per node colour. */
export const LEGEND_SWATCH_PALETTE = NODE_COLOR_KEYS.map(key => ({
  key,
  fill: NODE_COLORS[key].backgroundColor,
  border: NODE_COLORS[key].borderColor
}));

/** Node shape — what the box's outline says about the step it holds. */
export const NODE_SHAPES = {
  /** A process step. The default. */
  RECTANGLE: 'rect',
  /** A start/end terminal. */
  OVAL: 'oval',
  /** A binary decision. */
  HEXAGON: 'hexagon'
} as const;

/** How a Q&A element is answered. */
export const QUESTION_INPUTS = {
  /** One answer, from a short list of options the source defines. */
  RADIO: 'radio',
  /** Any number of answers, from a list of options the source defines. */
  CHECKBOX: 'checkbox',
  /** One answer, from a list too long to read as buttons. */
  DROPDOWN: 'dropdown',
  /** A numeric answer, with a unit when the source states one. */
  NUMBER: 'number',
  /** Free text, for a genuinely open-ended answer. */
  TEXT: 'text'
} as const;
export type QuestionInput = (typeof QUESTION_INPUTS)[keyof typeof QUESTION_INPUTS];

/** The inputs that answer from a defined option list, so options are required. */
export const OPTION_QUESTION_INPUTS: QuestionInput[] = [
  QUESTION_INPUTS.RADIO,
  QUESTION_INPUTS.CHECKBOX,
  QUESTION_INPUTS.DROPDOWN
];

/** Whether an orderable is a single order or a named bundle the EHR expands. */
export const ORDERABLE_KINDS = {
  /** One orderable — a drug, a lab, an imaging study, a procedure. */
  SINGLE: 'single',
  /** A named bundle the EHR expands into many orders: an order set, power plan, or SmartSet. */
  SET: 'set'
} as const;
export type OrderableKind = (typeof ORDERABLE_KINDS)[keyof typeof ORDERABLE_KINDS];

/** The doc's root maps, by the name each is stored under. */
export const DOC_MAPS = {
  NODES: 'nodes',
  EDGES: 'edges',
  META: 'meta',
  START: 'start',
  EHR: 'ehr',
  NOTE_TEMPLATES: 'noteTemplates',
  OVERVIEW: 'overview',
  /** On the draft-state doc, not the flowchart doc. */
  UNPLACED: 'unplaced'
} as const;
